<template>
  <b-container fluid>
    <h3>Users</h3>

    <b-row v-if="!selectedUser">
      <b-col class="d-flex justify-start">
        <b-button
          variant="blue"
          @click="addUser"
          size="sm"
          v-if="AuthType === 'sa'"
        >
          <b-icon-plus />
          Add a New User
        </b-button>
      </b-col>
    </b-row>
    <hr v-if="!selectedUser" />
    <b-row>
      <b-col v-if="!selectedUser">
        <!-- Show Active/Inactive Users -->
        <b-row>
          <b-col class="d-flex justify-content-end">
            <label class="mt-2 mr-2">User Status:</label>
            <b-radio-group class="mt-2" v-model="showActive">
              <b-radio :value="true">Active</b-radio>
              <b-radio :value="false">Inactive</b-radio>
            </b-radio-group>
          </b-col>
        </b-row>
        <!-- User Grid -->
        <dx-data-grid
          :data-source="users"
          :selection="gridSettings.selection"
          :allowColumnResizing="true"
          :columns="gridFields"
          height="725"
          :onRowDblClick="onSelection"
          :filter-row="gridSettings.filterRow"
          :search-panel="gridSettings.searchPanel"
          :remote-operations="gridSettings.remoteOps"
          :paging="gridSettings.paging"
          :pager="gridSettings.pager"
          :loadPanel="{ enabled: true, showPane: true }"
          ref="grd"
        >
        </dx-data-grid>
      </b-col>
      <b-col v-if="selectedUser">
        <b-overlay :show="submitting">
          <template v-slot:overlay>
            <div class="text-center">
              <b-icon-stopwatch
                font-scale="3"
                animation="cylon"
              ></b-icon-stopwatch>
              <p id="cancel-label">Saving, please wait...</p>
            </div>
          </template>
          <!-- Form -->
          <b-form
            :class="{
              'needs-validation': !submitted,
              'was-validated': submitted
            }"
            novalidate
            ref="f"
          >
            <b-container fluid>
              <b-row class="mt-3 mb-3 mr-1"> </b-row>
              <h5 v-if="!selectedUser.is_internal">
                Account Information
              </h5>
              <b-row>
                <b-col> </b-col>
              </b-row>
              <hr v-if="!selectedUser.is_internal" />
              <b-row v-if="!selectedUser.is_internal">
                <b-col>
                  <b-container fluid>
                    <b-form-group
                      :label-cols="2"
                      label="Site:"
                      label-align="right"
                    >
                      <b-select
                        v-model="selectedUser.site_id"
                        :options="options"
                        v-if="!selectedUser.is_internal"
                        :disabled="sites.length <= 0"
                        required
                      ></b-select>
                    </b-form-group>
                  </b-container>
                </b-col>
              </b-row>
              <hr />

              <h5>Personal Information</h5>
              <hr />
              <b-row>
                <b-col>
                  <b-form-group
                    :label-cols="2"
                    label="First Name:"
                    label-align="right"
                    :invalid-feedback="validationStat.firstName.msg"
                  >
                    <b-form-input
                      type="text"
                      v-model="selectedUser.first_name"
                      placeholder="First Name"
                      :maxlength="255"
                      @blur="trimField('firstName', $event)"
                      :state="validationStat.firstName.state"
                      required
                      id="fn"
                      ref="first"
                    />
                  </b-form-group>
                  <b-form-group
                    :label-cols="2"
                    label="Last Name:"
                    label-align="right"
                    :invalid-feedback="validationStat.lastName.msg"
                  >
                    <b-input
                      type="text"
                      v-model="selectedUser.last_name"
                      placeholder="Last Name"
                      :maxlength="255"
                      @blur="trimField('lastName', $event)"
                      :state="validationStat.lastName.state"
                      required
                      ref="last"
                    />
                  </b-form-group>

                  <b-form-group
                    :label-cols="2"
                    label="Email:"
                    label-align="right"
                    :invalid-feedback="validationStat.email.msg"
                  >
                    <b-input
                      type="email"
                      pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                      :state="validationStat.email.state"
                      v-model="selectedUser.email"
                      placeholder="user@yourdomain.com"
                      @blur="trimField('email', $event)"
                      :maxlength="255"
                      required
                      ref="email"
                    />
                  </b-form-group>
                  <b-form-group
                    :label-cols="2"
                    label="Phone:"
                    label-align="right"
                    :invalid-feedback="validationStat.phone.msg"
                  >
                    <b-input
                      type="tel"
                      pattern="[(][0-9]{3}[)] [0-9]{3}-[0-9]{4}"
                      :state="validationStat.email.state"
                      v-model="selectedUser.phoneNo"
                      placeholder="(000) 000-0000"
                      @blur="trimField('phone', $event)"
                      @change="phoneFormat"
                      :maxlength="255"
                      :min="13"
                      ref="phone"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <hr />
              <b-form-group label="Status:">
                <b-radio-group v-model="selectedUser.is_active">
                  <b-radio :value="1">Active</b-radio>
                  <b-radio :value="0">Inactive</b-radio>
                </b-radio-group>
              </b-form-group>
              <hr />
              <h5 v-if="AuthType === 'ca' || AuthType === 'sa'">
                User Type & Permissions
              </h5>
              <b-row v-if="AuthType === 'sa' || AuthType === 'ca'">
                <b-col>
                  <b-container fluid>
                    <b-row>
                      <b-col>
                        <b-form-invalid-feedback
                          :state="validationStat.userType.state"
                        >
                          {{ validationStat.userType.msg }}
                        </b-form-invalid-feedback>
                        <b-radio-group
                          v-model="selectedUser.is_internal"
                          v-if="AuthType === 'sa'"
                          required
                          ref="ut"
                        >
                          <b-radio name="Internal" :value="0">
                            Customer
                          </b-radio>
                          <b-radio name="Internal" :value="1">
                            LINK Document Services Group Internal
                          </b-radio>
                        </b-radio-group>
                        <b-checkbox
                          class="mt-1"
                          @change="setAdmin"
                          :checked="selectedUser.is_admin === 1"
                          v-if="AuthType === 'sa' || AuthType === 'ca'"
                        >
                          Administrator
                        </b-checkbox>
                      </b-col>
                    </b-row>
                    <hr />
                  </b-container>
                </b-col>
              </b-row>
              <b-row>
                <b-col class="d-flex justify-content-end" :md="4">
                  <b-button
                    type="submit"
                    href="#"
                    @click.stop.prevent="onSaveChangesClicked"
                    variant="blue"
                    style="width:125px;"
                  >
                    Save
                  </b-button>
                </b-col>
                <b-col class="d-flex justify-content-end" :md="4">
                  <b-button
                    variant="orange"
                    @click="onCancelClick"
                    style="width:125px;"
                  >
                    Cancel
                  </b-button>
                </b-col>
                <b-col class="d-flex justify-content-end">
                  <b-button
                    class="text-white"
                    variant="warning"
                    @click="onWelcomeClick"
                    v-if="selectedUser.id"
                  >
                    <b-icon-envelope class="mb-1" /> Send welcome email
                  </b-button>
                </b-col>
              </b-row>
            </b-container>
          </b-form>
        </b-overlay>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
export default {
  props: ["id"],
  data() {
    return {
      submitting: false,
      gridFields: [
        {
          dataField: "first_name",
          caption: "Name",
          calculateDisplayValue: e => e.first_name + " " + e.last_name,
          calculateSortValue: "last_name"
        },
        {
          dataField: "last_name",
          sortOrder: "asc",
          visible: false
        },
        {
          caption: "Account>>Primary Location",
          calculateDisplayValue: this.accountDisplay
        },
        {
          dataField: "is_admin",
          caption: "Admin",
          alignment: "center",
          width: 100,
          calculateDisplayValue: this.adminDisplay
        },
        {
          caption: "Has Access To Locations",
          dataField: "is_internal",
          width: 200,
          alignment: "center",
          calculateDisplayValue: e => (e.is_admin == 1 ? "All" : ""),
          lookup: {
            dataSource: () => [
              {
                id: 0,
                text: ""
              },
              {
                id: 1,
                text: "All"
              }
            ],
            displayExpr: "text",
            valueExpr: "id"
          }
        }
      ],
      formFields: [
        {
          dataField: "first_name",
          editorType: "dxTextBox",
          editorOptions: {
            maxLength: 255
          }
        },
        {
          dataField: "last_name",
          editorType: "dxTextBox",
          editorOptions: {
            maxLength: 255
          }
        },
        {
          dataField: "email",
          editorType: "dxTextBox",
          isRequired: true,
          editorOptions: {
            mode: "email",
            maxLength: 255,
            onFocusOut: this.trimField
          }
        }
      ],
      gridSettings: {
        selection: {
          mode: "single"
        },
        headerFilter: {
          allowSearch: true,
          width: 150,
          visible: false
        },
        filterRow: {
          visible: false
        },
        searchPanel: {
          highlightSearchText: true,
          placeholder: "Search...",
          text: "",
          visible: true,
          width: 250
        },
        pager: {
          showPageSizeSelector: true,
          showInfo: true,
          allowedPageSizes: [25, 50, 100]
        },
        paging: {
          pageSize: 25
        },
        remoteOps: {
          paging: true,
          filtering: false,
          summary: false,
          groupPaging: true
        }
      },
      validationStat: {
        site: {
          state: null,
          msg: ""
        },
        firstName: {
          state: null,
          msg: ""
        },
        lastName: {
          state: null,
          msg: ""
        },
        email: {
          state: null,
          msg: ""
        },
        phone: {
          state: null,
          msg: ""
        },
        userType: {
          state: null,
          msg: ""
        }
      },
      selectedUser: null,
      isNewUser: false,
      submitted: false,
      isValidating: false
    };
  },
  beforeMount() {
    if (this.id) {
      var users = this.$store.getters["sysUsers/users"];
      this.selectedUser = users.find(u => u.id === parseInt(this.id));
    }
  },
  mounted() {
    this.$refs.grd.$_instance.repaint();
  },
  computed: {
    showActive: {
      get: function() {
        debugger;
        if (!this.$store) {
          return false;
        }
        return !this.$store.getters["sysUsers/showInactive"];
      },
      set: function(val) {
        debugger;
        this.$store.dispatch("sysUsers/showInactive", !val).then(() => {
          debugger;
          this.$refs.grd.$_instance.refresh();
        });
      }
    },
    isLoading() {
      var stat = this.$store.getters["isLoading"];
      return stat;
    },
    users() {
      if (!this.$store) {
        return [];
      }
      var store = this.$store.getters["sysUsers/store"];

      return store;
    },
    sites() {
      return this.$store.getters["accounts/allSites"];
    },
    AuthType() {
      return this.$store.getters["user/authType"];
    },
    accounts() {
      return this.$store.getters["accounts/accounts"];
    },
    options() {
      var acct = this.accounts ? this.accounts : [];
      var sites = this.sites ? this.sites : [];
      if (acct.length === 0 || sites.length === 0) {
        return [];
      }
      var rsp = [
        {
          value: null,
          text: "Please select a site"
        }
      ];

      //Sort accounts by name:
      acct.sort();

      acct.forEach(ac => {
        debugger;
        var r = {
          label: ac.name,
          options: []
        };
        var st = ac.sites;

        st = st ? st : [];

        //Sort sites by name:
        st.sort(s => s.name);

        if (st.length > 0) {
          st.forEach(s => {
            var site = {
              value: s.id,
              text: s.name
            };

            r.options.push(site);
            r.options.sort((a, b) => {
              if (a && b) {
                var x = a.text.toLowerCase();
                var y = b.text.toLowerCase();
                if (x < y) {
                  return -1;
                }
                if (x > y) {
                  return 1;
                }
              }
              return 0;
            });
          });
          rsp.push(r);
          rsp.sort((a, b) => {
            debugger;
            if (a && b && a.label && b.label) {
              var x = a.label.toLowerCase();
              var y = b.label.toLowerCase();
              if (x < y) {
                return -1;
              }
              if (x > y) {
                return 1;
              }
            }
            return 0;
          });
        }
      });
      debugger;
      rsp.sort(a => a.label);
      return rsp;
    }
  },
  methods: {
    onWelcomeClick(e) {
      debugger;
      e.preventDefault();
      var id = this.selectedUser.id;
      this.submitting = true;
      return this.$store
        .dispatch("sysUsers/sendWelcomeMsg", id)
        .then(() => this.reset());
    },
    addUser() {
      this.selectedUser = {
        first_name: "",
        last_name: "",
        phoneNo: "",
        email: "",
        site_id: null,
        is_active: 1
      };
      this.isNewUser = true;
    },
    refresh() {
      // this.debugUserType();
    },
    onSaveChangesClicked(e) {
      debugger;
      e.preventDefault();
      // this.validate();

      this.submitted = true;
      if (!this.$refs.f.checkValidity()) {
        //First Name:
        this.validationStat.firstName.state = this.$refs.first.checkValidity();
        this.validationStat.firstName.msg = this.$refs.first.validationMessage;

        //Last Name:
        this.validationStat.lastName.state = this.$refs.last.checkValidity();
        this.validationStat.lastName.msg = this.$refs.last.validationMessage;

        //Email:
        this.validationStat.email.state = this.$refs.email.checkValidity();
        this.validationStat.email.msg = this.$refs.email.validationMessage;

        debugger;

        // //userType:
        // this.validationStat.userType.state = this.$refs.ut.checkValidity();
        // this.validationStat.userType.msg = this.$refs.ut.validationMessage;

        return;
      }
      this.submitting = true;

      switch (this.isNewUser) {
        case true:
          return this.$store
            .dispatch("sysUsers/registerUserAsync", this.selectedUser)
            .then(rsp => {
              debugger;
              this.$store.dispatch("sysUsers/refreshUsersAsync").then(() => {
                if (!rsp) {
                  this.submitting = false;
                  return;
                }
                this.isNewUser = false;
                this.reset();
                this.selectedUser = null;
              });
            });

          break;
        default:
          this.$store
            .dispatch("sysUsers/updateUserAsync", this.selectedUser)
            .then(rsp => {
              debugger;
              this.$store.dispatch("sysUsers/refreshUsersAsync").then(() => {
                debugger;
                if (!rsp) {
                  this.submitting = false;
                  return;
                }
                this.reset();
                this.selectedUser = null;
              });
            });
          break;
      }
    },
    onCancelClick() {
      this.reset();
    },
    reset(e = null) {
      if (e) {
        e.preventDefault();
      }

      this.refresh();
      this.submitted = false;
      this.submitting = false;
      this.isNewUser = false;
      this.isEmailBlured = false;
      this.isEmailValid = false;
      this.selectedUser = null;
      this.validationStat = {
        site: {
          state: null,
          msg: ""
        },
        firstName: {
          state: null,
          msg: ""
        },
        lastName: {
          state: null,
          msg: ""
        },
        email: {
          state: null,
          msg: ""
        },
        phone: {
          state: null,
          msg: ""
        },
        userType: {
          state: null,
          msg: ""
        }
      };
    },
    onSelection(e) {
      debugger;
      this.selectedUser = e.data; //e.selectedRowsData[0];
    },
    adminDisplay(e) {
      return e.is_admin == 1 ? "Yes" : "No";
    },
    accountDisplay(e) {
      var site = this.sites.find(s => s.id === e.site_id);
      if (!site) {
        return "";
      }
      var acct = this.accounts.find(a => a.id == site.account_id);

      if (!acct || !site) {
        return "";
      }

      return acct.name + ">>" + site.name;
    },
    trimField(field = null, el) {
      debugger;
      this.selectedUser.first_name = this.selectedUser.first_name.trim();
      this.selectedUser.last_name = this.selectedUser.last_name.trim();
      this.selectedUser.email = this.selectedUser.email.trim();

      if (field) {
        this.validating(field, el);
      }
    },
    phoneFormat(phone) {
      debugger;
      if (!this.selectedUser || !this.selectedUser.phoneNo) {
        this.selectedUser.phoneNo = "";
      }

      this.selectedUser.phoneNo = this.selectedUser.phoneNo
        .replace(/[^0-9]/g, "")
        .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    },
    validating(field, el) {
      debugger;

      var fld = this.validationStat[field];

      debugger;
      fld.state =
        el.srcElement.checkValidity() &&
        this.validateEmail(this.selectedUser.email);
      fld.msg = el.srcElement.validationMessage;
    },
    validateEmail(email) {
      if (!this.isEmailBlured) {
        return null;
      }
      debugger;
      var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },
    setAdmin(e) {
      this.selectedUser.is_admin = e ? 1 : 0;
    },
    debugUserType() {
      return this.$store.dispatch("accounts/getAccounts").then(() => {
        return this.$store.dispatch("sysUsers/refreshUsersAsync");
      });
    }
  }
};
</script>

<style scoped>
input {
  margin-bottom: 5px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/admin/users.vue